import React, { useState, useEffect, useCallback } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import NumberFormat from 'react-number-format';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';

import { listUsers } from 'services/users/userServices';
import { createNotes } from 'services/CaseNoteServices';
import { getNestedProperty, formatContactList, sortByKey, sanitizeHtmlColorContent, toCapitalized, formatPhoneNumber } from 'utils/utils';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { totalCountEvent } from 'services/generalServices';
import { generateFullName } from 'utils/generateFullNameUtils';
import { ModalHeader } from 'shared/ModalHeader';
import { callTypeOptions, titles, types } from 'constants/dropdownOptions';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import { ChangeCaseUserData } from '../Helpers/ChangeCaseUserData';
import { formatCreateNote } from '../Helpers/formatCreateNote';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { RadioButton } from 'primereact/radiobutton';
import { Tooltip } from 'primereact/tooltip';
import { createContact } from 'services/contact';
import { updateContactDetails } from 'services/ContactService';
import { validatePhoneNumber } from 'modules/lead/helpers/helpers';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import ContactDropdownField from 'modules/contact/components/ContactAddPage/components/ContactDropdownField';
import { Tag } from 'primereact/tag';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import { getGlobalDropdown } from 'services/caseLeadDropdownService';

const itemTemplate = (e) => (
  <div className="d-flex align-items-center justify-content-between">
    <div className="d-flex flex-column align-items-start">
      <span>{generateFullName(e)}</span>
      <span className="text-muted F-size12">{formatPhoneNumber(e?.phone)}</span>
    </div>
    <div className="text-muted F-size12">
      {e.contact_type === 'business' && (
        <Tag className="y-tag" value={e.contact_type.charAt(0).toUpperCase() + e.contact_type.slice(1)} severity="normal"></Tag>
      )}
    </div>
  </div>
);

const selectedItemTemplate = (e) => generateFullName(e);

const autocompleteValidation = (e) => (typeof e === 'object' ? true : 'Field Required');

const NotesCreate = (props) => {
  const {
    getCaseDetails,
    leadDetails,
    companionLink,
    companionList,
    isLeadSpecific,
    caseDetails,
    isFromDashboard,
    setShow,
    fetchCaseNotes,
    onMountUnMount,
    onOperationBreak,
    noteType = 'case_note',
    isFromCaseSpecificModule,
  } = props;
  let { id } = useParams();
  const { addToast } = useToast();
  const history = useHistory();
  const case_id = leadDetails?.case_id || caseDetails?.case_id || id;

  const [userList, setUserList] = useState([]);

  const [filteredClients, setFilteredClients] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateContactFields, setsShowCreateContactFields] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');
  const [updatePrimaryNumber, setUpdatePrimaryNumber] = useState(false);
  const [tempPhoneNumber, setTempPhoneNumber] = useState('');

  useEffect(() => {
    typeof onMountUnMount === 'function' && onMountUnMount('create-note', 'mounted', case_id);
    return () => {
      typeof onOperationBreak === 'function' && onOperationBreak();
    };
  }, []);

  useEffect(() => {
    loadUserList();
  }, [case_id]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []); // to remove the dropdown scroll issue in dialog dropdown

  const handleClose = () => {
    setShow(null);
    history.replace({ pathname: history.location.pathname, search: '', hash: history.location.hash });
  };

  const loadUserList = () => {
    totalCountEvent('user').then((response) => {
      listUsers({ is_include_groups: true, is_confirmed_user: true, status: 'ACTIVE', limit: response.data, active_groups: true })
        .then((response) => setUserList(response.data?.firm_users || []))
        .catch((error) => console.log(error));
    });
  };

  const formatUserList = useCallback(
    (isGroup) => {
      if (isGroup) {
        const formattedList = formatContactList(userList);
        const sortedList = formattedList?.map((item) => {
          if (item?.code === 'Users') {
            // Sort Users by first_name alphabetically
            return {
              ...item,
              items: item.items.sort((a, b) => {
                const nameA = (a?.first_name || '').toLowerCase();
                const nameB = (b?.first_name || '').toLowerCase();
                return nameA.localeCompare(nameB);
              }),
            };
          } else if (item?.code === 'Groups') {
            // Sort Groups by group_name/assignee_name alphabetically
            return {
              ...item,
              items: item.items.sort((a, b) => {
                const nameA = (a?.group_name || a?.assignee_name || '').toLowerCase();
                const nameB = (b?.group_name || b?.assignee_name || '').toLowerCase();
                return nameA.localeCompare(nameB);
              }),
            };
          }
          return item;
        });
        return ChangeCaseUserData(sortedList || []);
      } else {
        const filterDataWithoutGroups = userList?.filter((item) => item?.res_type !== 'user-group') || [];
        // Sort individual users by first_name alphabetically
        const sortedUsers = filterDataWithoutGroups.sort((a, b) => {
          const nameA = (a?.first_name || '').toLowerCase();
          const nameB = (b?.first_name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        return ChangeCaseUserData(sortedUsers);
      }
    },
    [userList]
  );

  const handleSaveNote = (data) => {
    setLoading(true);
    const caseName = caseDetails?.case_description?.case_name || data?.case_id?.case_name;
    let _case_id = case_id || data?.case_id?.id;
    const selectedIsLead = isLeadSpecific || Boolean(data?.case_id?.is_lead);

    const phoneValue = getValues('phone');
    const phoneNeedsUpdate = updatePrimaryNumber;
    const additionalPhoneNeeded =
      selectedContact?.phone !== phoneValue &&
      (!selectedContact?.additional_phone || selectedContact?.additional_phone === '' || selectedContact?.additional_phone === null);
    if (phoneNeedsUpdate || additionalPhoneNeeded) {
      if (selectedContact && typeof selectedContact === 'object') {
        const { is_new, ...restContactData } = selectedContact;
        const sendData = {
          ...restContactData,
          ...(phoneNeedsUpdate ? { phone: phoneValue } : { additional_phone: phoneValue }),
        };

        updateContactDetails(sendData)
          .then(() => {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CONTACT_UPDATE_SUCCESS);
          })
          .catch((err) => {
            let msg = toastConstant.message.CONTACT_UPDATE_FAILED;
            if (err?.response?.data) msg = toCapitalized(err.response.data);
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, msg);
          });
      }
    }

    createNotes({
      data: formatCreateNote({
        ...data,
        case_id: _case_id,
        case_name: caseName,
        is_lead: selectedIsLead,
        note_type: noteType,
        companion_link: data?.companion_link && companionList?.length > 0 ? true : false,
      }),
      is_lead: selectedIsLead,
    })
      .then((response) => {
        typeof onMountUnMount === 'function' && onMountUnMount('create-note', 'unmounted', _case_id);
        fetchCaseNotes({});
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.NOTE_SUCCESS);
        if (isFromDashboard) {
          const modulePath = selectedIsLead ? 'leads' : 'cases';
          const notesPath = selectedIsLead ? 'note' : 'notes';
          history.push(`/${modulePath}/${_case_id}/${notesPath}`);
        }
        handleClose();
        if (isFromCaseSpecificModule || isLeadSpecific) {
          getCaseDetails();
        }
      })
      .catch((error) => {
        typeof onOperationBreak === 'function' && onOperationBreak();
        console.log(error);
      })
      .finally(() => setLoading(false));
  };

  const searchAsync = async (e) => {
    try {
      const rawQuery = e?.query ?? '';
      const hasLetters = /[a-zA-Z]/.test(rawQuery);
      const isPhoneQuery = !hasLetters && /\d/.test(rawQuery);

      let keyWord = rawQuery;
      if (isPhoneQuery) {
        keyWord = rawQuery?.replace(/[^0-9.]/g, '')?.substring(0, 10);
      } else {
        keyWord = rawQuery;
      }

      const response = await getGlobalDropdown(
        '',
        'contact',
        keyWord,
        '',
        false,
        1,
        20,
        'alphabet_ascending',
        '',
        undefined,
        false,
        '',
        '',
        false,
        true
      );

      const sortedContacts =
        response?.data?.data?.sort((a, b) => {
          const aName = a?.contact_type === 'person' ? a?.first_name?.toLowerCase() || '' : a?.title?.toLowerCase() || '';
          const bName = b?.contact_type === 'person' ? b?.first_name?.toLowerCase() || '' : b?.title?.toLowerCase() || '';
          return aName.localeCompare(bName);
        }) || [];

      setFilteredClients(sortedContacts);

      if (sortedContacts.length === 0 && rawQuery) {
        const cleanQuery = keyWord.replace(/\D/g, '');

        if (cleanQuery.length > 0 && !hasLetters) {
          setTempPhoneNumber(cleanQuery);
        } else {
          setTempPhoneNumber('');
        }
      } else {
        setTempPhoneNumber(''); // Clear temp phone if results found
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    getValues,
    setValue,
    trigger,
    reset,
    clearErrors,
  } = useForm();

  const contactType = useWatch({ control, name: 'contact_type' });

  const ADD_CONTACT_FIELDS = ['contact_title', 'contact_first_name', 'contact_last_name', 'title', 'contact_phone'];

  useEffect(() => {
    if (!showCreateContactFields || !contactType || !tempPhoneNumber) {
      return;
    }
    const timeoutId = setTimeout(() => {
      setValue('contact_phone', tempPhoneNumber, { shouldValidate: true });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [showCreateContactFields, contactType, tempPhoneNumber, setValue]);

  const getAddContactValidationFields = (contactTypeValue) => {
    if (contactTypeValue === 'person') {
      return ['contact_type', 'contact_title', 'contact_first_name', 'contact_last_name', 'contact_phone'];
    }
    if (contactTypeValue === 'business') {
      return ['contact_type', 'title', 'contact_phone'];
    }
    return ['contact_type'];
  };

  const handleContactTypeChange = (contactTypeValue) => {
    clearErrors(ADD_CONTACT_FIELDS);
    reset({
      ...getValues(),
      contact_type: contactTypeValue,
      contact_title: '',
      contact_first_name: '',
      contact_last_name: '',
      title: '',
      contact_phone: '',
    });
  };

  const handleContactPhoneValueChange = (onChange, phoneValue) => {
    onChange(phoneValue);
    if (!selectedContact?.is_new) {
      setUpdatePrimaryNumber(false);
    }
    if (phoneValue) {
      trigger('contact_phone');
    }
  };

  const handleExistingContactPhoneValueChange = (onChange, phoneValue) => {
    onChange(phoneValue);
    if (!selectedContact?.is_new) {
      setUpdatePrimaryNumber(false);
    }
    if (phoneValue) {
      trigger('phone');
    }
  };

  const cancelAddContactFields = () => {
    const callType = getValues('call_type');
    const contactField = callType === 'incoming' ? 'called_by' : 'person_called';
    const restoredContact = typeof selectedContact === 'object' && selectedContact ? selectedContact : '';

    setsShowCreateContactFields(false);
    setTempPhoneNumber('');
    clearErrors(ADD_CONTACT_FIELDS);
    reset({
      ...getValues(),
      contact_title: '',
      contact_first_name: '',
      contact_last_name: '',
      title: '',
      contact_phone: '',
      contact_type: '',
      [contactField]: restoredContact,
      phone: restoredContact?.phone || '',
    });
  };

  const resolutionStatus = useWatch({ control, name: 'resolution_status' });

  const RequiredValidation = ({ field, message = 'Field Required' }) => {
    return getNestedProperty(errors, field) ? (
      <InputErrorMessage>{getNestedProperty(errors, field)?.message || message}</InputErrorMessage>
    ) : null;
  };

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {option?.assignee_name}
      </>
    );
  };

  const assignToCallLogItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {option?.label}
      </>
    );
  };

  const assignToCallForItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {option?.label}
      </>
    );
  };

  const getNormalizedContact = (response) => {
    const data = response?.data || {};

    return {
      contact_id: data.contact_id,
      last_updated: data.last_updated,
      status: data.status,
      time: data.time,
      is_create_client_portal: data.is_create_client_portal,
      profile_image: data.profile_image,
      contact_type: data.contact_type,
      last_name: data.last_name,
      res_type: data.res_type,
      first_name: data.first_name,
      sk: data.sk,
      type_tenant: data.type_tenant,
      pk: data.pk,
      country_code: data.country_code,
      title: data.title,
      is_new: true,
      phone: data.phone,
    };
  };

  const handleCreateContact = async () => {
    const contactTypeValue = getValues('contact_type');
    const isValid = await trigger(getAddContactValidationFields(contactTypeValue));
    if (isValid) {
      const values = getValues();
      let data = {};
      if (values?.contact_type === 'person') {
        data = {
          contact_type: values?.contact_type,
          title: values?.contact_title,
          first_name: values?.contact_first_name,
          last_name: values?.contact_last_name,
          phone: values?.contact_phone,
        };
      } else if (values?.contact_type === 'business') {
        data = {
          contact_type: values?.contact_type,
          title: values?.title,
          phone: values?.contact_phone,
        };
      } else {
        return;
      }

      createContact(data)
        .then((response) => {
          setsShowCreateContactFields(false);
          setTempPhoneNumber('');
          clearErrors(ADD_CONTACT_FIELDS);
          reset({
            ...values,
            contact_title: '',
            contact_first_name: '',
            contact_last_name: '',
            title: '',
            contact_phone: '',
          });

          const normalizedContact = getNormalizedContact(response);
          setValue('phone', '');
          setSelectedContact(normalizedContact);
          setUpdatePrimaryNumber(true);
          getValues('call_type') === 'incoming' ? setValue('called_by', normalizedContact) : setValue('person_called', normalizedContact);
          setValue('phone', normalizedContact?.phone ? normalizedContact?.phone : '');
          setValue('contact_type', '');
          setFilteredClients((prev) => [...(prev || []), normalizedContact]);
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CONTACT_CREATE_SUCCESS);
        })
        .catch((error) => {
          let errMsg = toastConstant.message.CONTACT_CREATE_FAILED;
          if (error?.response?.data) errMsg = error?.response?.data;
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, errMsg);
        });
    }
  };

  const getFormErrorMessage = (name) => {
    const nameParts = name.split(/[\.\[\]]/).filter(Boolean);
    let errorObj = errors;

    for (let part of nameParts) {
      if (!errorObj) break;
      errorObj = errorObj[part];
    }

    return errorObj ? <small className="p-error">{errorObj.message}</small> : null;
  };

  return (
    <>
      <GlobalLoader />
      <Dialog className="new-task-popup" visible onHide={handleClose} header={ModalHeader} footer={<></>} style={{ width: '45vw' }}>
        <div className="row">
          <div className="col-12 col-lg-12 d-flex justify-content-between pb-3">
            <div className="d-flex align-items-center justify-content-start">
              <h5 className="sub-title mb-0">{`Create New ${noteType === 'case_note' ? 'Case Note' : 'Call Log'}`}</h5>
            </div>
          </div>
        </div>

        <div>
          {companionList?.length > 0 && (
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-12">
                <Controller
                  name="companion_link"
                  control={control}
                  defaultValue={companionLink}
                  render={({ field: { name, value, onChange } }) => (
                    <div className="d-flex align-items-center">
                      <label htmlFor="companion_link" className="me-2">
                        {`Add this ${noteType === 'case_note' ? 'Case Note' : 'Call Log'} to all companion cases (${
                          Array.isArray(companionList) ? companionList.length : 0
                        } ${(Array.isArray(companionList) ? companionList.length : 0) === 1 ? 'case' : 'cases'})`}
                      </label>
                      <Checkbox inputId="companion_link" name={name} onChange={(e) => onChange(e.checked)} checked={value} />
                    </div>
                  )}
                />
              </div>
            </div>
          )}

          {isFromDashboard && (
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12 d-flex align-items-center p-medium">
                Case/Lead
                <span className="alert-star"> *</span>
              </div>
              <div className="col-md-8 col-12">
                <Controller
                  name="case_id"
                  control={control}
                  rules={{ required: 'Case is required' }}
                  render={({ field: { value, onChange, ref } }) => (
                    <CaseListDropDown
                      isClient={false}
                      value={value}
                      onChange={(e) => {
                        if (value !== e) {
                          onChange(e);
                        }
                      }}
                      name="case"
                      placeholder="Case/Lead Name"
                      noOptionsMessage="No Cases/Leads Found"
                      isClearable
                      ref={ref}
                    />
                  )}
                />
                <RequiredValidation field={'case_id'} />
              </div>
            </div>
          )}

          {noteType === 'case_note' ? (
            <>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12  p-medium">
                  Title <span className="alert-star"> *</span>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: 'Title is Required' }}
                    render={({ field }) => (
                      <TextSnippetWrapper id={field.name} placeholder="Enter Title" type="text" className="w-100 input-shadow" {...field} />
                    )}
                  />
                  <RequiredValidation field={'title'} />
                </div>
              </div>
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">
                  Details<span className="alert-star"> *</span>
                </div>
                <div className="col-sm-8 col-12 email-quill-reply">
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: 'Description is Required' }}
                    className="shadow-small"
                    render={({ field: { onChange, value, ...rest } }) => (
                      <TextSnippetWrapper
                        type="editor"
                        placeholder="Enter details here"
                        className="email-quill-editor"
                        id="description"
                        value={sanitizeHtmlColorContent(value)}
                        onChange={(e) => {
                          onChange(e);
                        }}
                        {...rest}
                      />
                    )}
                  />
                  <RequiredValidation field={'description'} />
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-sm-4 p-medium">
                  <label htmlFor="cb1" className="p-checkbox-label">
                    Need Resolution
                  </label>
                </div>
                <div className="col-sm-8 d-flex align-items-center ml-sm-10">
                  <Controller
                    name="resolution_status"
                    control={control}
                    defaultValue={false}
                    render={({ field: { name, ref, onChange, value }, fieldState }) => (
                      <Checkbox
                        inputId={name}
                        ref={ref}
                        onChange={(e) => onChange(e.checked)}
                        checked={value}
                        className={{ 'p-invalid': fieldState.invalid }}
                      />
                    )}
                  />
                </div>
              </div>

              {/* CASENOTE 1 */}
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-sm-4 col-12 p-medium">
                  Assign to Firm User{resolutionStatus && <span className="alert-star"> *</span>}
                </div>
                <div className="col-sm-8 col-12">
                  <Controller
                    name="assign_to"
                    control={control}
                    rules={{ required: resolutionStatus ? 'Assign To is Required' : false }}
                    render={({ field: { value, onChange } }) => (
                      <MultiSelect
                        options={formatUserList(true) ?? []}
                        className="input-shadow w-100"
                        filter
                        placeholder="Select"
                        display="chip"
                        value={value}
                        onChange={(e) => {
                          onChange(e.value);
                        }}
                        optionValue={(v) => v}
                        optionGroupLabel="assignee_name"
                        optionLabel="assignee_name"
                        optionGroupChildren="items"
                        resetFilterOnHide
                        emptyFilterMessage="No records found"
                        showSelectAll={false}
                        itemTemplate={assignToItemTemplate}
                        disabled={!resolutionStatus}
                      />
                    )}
                  />
                  <RequiredValidation field={'assign_to'} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-sm-4 col-12 d-flex align-items-center p-medium">
                  Call Type <span className="alert-star"> *</span>
                </div>
                <div className="col-sm-8 col-12">
                  <Controller
                    name="call_type"
                    control={control}
                    rules={{ required: 'Call Type is Required' }}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <div className="d-flex gap-3 flex-wrap">
                        {callTypeOptions.map((option) => (
                          <div key={option.value} className="d-flex align-items-center">
                            <RadioButton
                              inputId={option.value}
                              name={name}
                              value={option.value}
                              onChange={(e) => {
                                onChange(e.value);
                                setsShowCreateContactFields(false);
                                setTempPhoneNumber('');
                                clearErrors(ADD_CONTACT_FIELDS);
                                reset({
                                  ...getValues(),
                                  contact_title: '',
                                  contact_first_name: '',
                                  contact_last_name: '',
                                  contact_type: '',
                                  title: '',
                                  contact_phone: '',
                                  called_by: '',
                                  caller_name: '',
                                  phone: '',
                                  contact_extension: '',
                                  person_called: '',
                                });
                              }}
                              checked={value === option.value}
                            />
                            <label htmlFor={option.value} className="ms-2">
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  />

                  <RequiredValidation field={'call_type'} />
                </div>
              </div>
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">
                  Details<span className="alert-star"> *</span>
                </div>
                <div className="col-sm-8 col-12 email-quill-reply">
                  <Controller
                    name="description"
                    control={control}
                    // rules={{ required: 'Description is Required' }}
                    rules={{
                      validate: {
                        required: (value) => {
                          // Check if value is empty or contains only empty HTML tags
                          if (!value || value.replace(/<[^>]*>/g, '').trim() === '') {
                            return 'Description is required';
                          }
                          return true;
                        },
                      },
                    }}
                    className="shadow-small"
                    render={({ field: { onChange, value, ...rest } }) => (
                      <TextSnippetWrapper
                        type="editor"
                        placeholder="Enter details here"
                        className="email-quill-editor"
                        id="description"
                        value={sanitizeHtmlColorContent(value)}
                        onChange={(e) => {
                          onChange(e);
                        }}
                        {...rest}
                      />
                    )}
                  />
                  <RequiredValidation field={'description'} />
                </div>
              </div>

              {watch('call_type') === 'incoming' && (
                <>
                  {!showCreateContactFields ? (
                    <div>
                      <div className="d-flex align-items-center py-2 flex-wrap">
                        <div className="col-sm-4 col-12  p-medium">
                          Called By<span className="alert-star"> * </span>
                        </div>
                        <div className="col-sm-8 col-12">
                          <div className="d-flex align-items-center">
                            <div style={{ flexGrow: 1 }}>
                              <Controller
                                name="called_by"
                                control={control}
                                rules={{ required: 'Called By is required', validate: autocompleteValidation }}
                                render={({ field: { value, onChange } }) => (
                                  <div style={{ position: 'relative' }}>
                                    <AutoComplete
                                      style={{ width: '100%' }}
                                      inputStyle={{ width: '100%' }}
                                      placeholder="Search by Phone Number or Caller Name"
                                      suggestions={filteredClients}
                                      itemTemplate={itemTemplate}
                                      selectedItemTemplate={selectedItemTemplate}
                                      completeMethod={searchAsync}
                                      className="w-100 input-shadow"
                                      label="first_name"
                                      onChange={(e) => {
                                        onChange(e.value);
                                        setValue('phone', e?.value?.phone ? e?.value?.phone : '');
                                        setSelectedContact(e?.value);
                                      }}
                                      value={value}
                                      onBlur={() => typeof value !== 'object' && onChange('')}
                                    />
                                    {watch('called_by') !== '' &&
                                      selectedContact &&
                                      typeof selectedContact === 'object' &&
                                      selectedContact?.contact_type === 'business' && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 6,
                                            pointerEvents: 'none',
                                            background: 'transparent',
                                            zIndex: 2,
                                          }}
                                        >
                                          <Tag
                                            className="y-tag"
                                            value={
                                              selectedContact.contact_type.charAt(0).toUpperCase() + selectedContact.contact_type.slice(1)
                                            }
                                            severity="normal"
                                          ></Tag>
                                        </div>
                                      )}
                                  </div>
                                )}
                              />
                              <RequiredValidation field={'called_by'} />
                            </div>
                            <i
                              className="fas fa-plus-circle add-contact pointer ms-2"
                              onClick={() => {
                                setsShowCreateContactFields(true);
                              }}
                            ></i>
                            <Tooltip content="Add Contact" position="top" target=".add-contact" showDelay={500} />
                          </div>
                        </div>
                      </div>
                      {watch('called_by') !== '' && selectedContact && typeof selectedContact === 'object' && (
                        <div>
                          {selectedContact?.contact_type === 'business' && (
                            <div className="d-flex py-2 flex-wrap align-items-start">
                              <label className="col-sm-4 col-12 p-medium mt-2">
                                Caller Name <span className="alert-star">*</span>
                              </label>
                              <div className="col-sm-8 col-12 d-flex flex-column">
                                <Controller
                                  name="caller_name"
                                  control={control}
                                  rules={{ required: 'Required' }}
                                  render={({ field }) => (
                                    <TextSnippetWrapper
                                      {...field}
                                      type="text"
                                      value={field.value}
                                      placeholder="Enter Caller Name"
                                      onChange={(e) => {
                                        const trimmedValue = e.target.value.trimStart();
                                        field.onChange(trimmedValue);
                                        trigger('caller_name');
                                      }}
                                      className="input-shadow w-100"
                                    />
                                  )}
                                />
                                <RequiredValidation field={'caller_name'} />
                              </div>
                            </div>
                          )}

                          <div className="d-flex align-items-center py-2 flex-wrap">
                            <div className="col-sm-4 col-12 p-medium">
                              Phone Number<span className="alert-star"> *</span>
                            </div>
                            <div className="col-sm-8 col-12">
                              <Controller
                                name="phone"
                                control={control}
                                rules={{
                                  required: 'Phone number is required',
                                  minLength: { value: 10, message: 'Min length should be 10' },
                                  validate: {
                                    asyncValidation: async (value) => {
                                      const isValid = await validatePhoneNumber(value, selectedContact?.contact_id);
                                      return isValid || 'Invalid phone number';
                                    },
                                  },
                                }}
                                render={({ field: { onChange, value } }) => (
                                  <NumberFormat
                                    className="input-shadow w-100 p-2 number-format"
                                    placeholder="Phone"
                                    format="(###) ###-####"
                                    mask="_"
                                    name="phone"
                                    onValueChange={(e) => {
                                      handleExistingContactPhoneValueChange(onChange, e.value);
                                    }}
                                    value={value}
                                  />
                                )}
                              />
                              <RequiredValidation field={'phone'} />
                            </div>
                          </div>
                          {selectedContact?.contact_type === 'business' && (
                            <div className="d-flex py-2 flex-wrap align-items-start">
                              <label className="col-sm-4 col-12 p-medium mt-2">
                                Extension <span className="alert-star">*</span>
                              </label>
                              <div className="col-sm-8 col-12 d-flex flex-column">
                                <Controller
                                  name="contact_extension"
                                  control={control}
                                  rules={{ required: 'Required' }}
                                  render={({ field }) => (
                                    <TextSnippetWrapper
                                      {...field}
                                      type="text"
                                      value={field.value}
                                      placeholder="Enter Extension"
                                      onChange={(e) => {
                                        const trimmedValue = e.target.value.trimStart();
                                        field.onChange(trimmedValue);
                                        trigger('contact_extension');
                                      }}
                                      className="input-shadow w-100"
                                    />
                                  )}
                                />
                                <RequiredValidation field={'contact_extension'} />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="inner-card p-3 my-2">
                      <h5 className="sub-title mb-2">{'Add New Contact'}</h5>
                      <div>
                        <ContactDropdownField
                          control={control}
                          disabled={false}
                          getFormErrorMessage={getFormErrorMessage}
                          name={'contact_type'}
                          onChange={handleContactTypeChange}
                          options={types.filter((type) => type.value !== 'location')}
                          placeholder={'Type'}
                          rules={{ required: 'Required' }}
                        />
                      </div>
                      {contactType !== '' && (
                        <div>
                          {contactType === 'person' ? (
                            <div key="add-contact-person">
                              <div className="d-flex py-2 flex-wrap align-items-start">
                                <label className="col-sm-4 col-12 p-medium mt-2">
                                  Title <span className="alert-star">*</span>
                                </label>
                                <div className="col-sm-8 col-12 d-flex flex-column">
                                  <Controller
                                    shouldUnregister
                                    name="contact_title"
                                    control={control}
                                    rules={{ required: 'Required' }}
                                    render={({ field: { name, value, onChange } }) => (
                                      <Dropdown
                                        id={name}
                                        options={titles}
                                        onChange={(e) => {
                                          onChange(e.target.value);
                                          trigger('contact_title');
                                        }}
                                        value={value}
                                        placeholder="Title"
                                        className="w-100"
                                        filter
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_title'} />
                                </div>
                              </div>

                              <div className="d-flex py-2 flex-wrap align-items-start">
                                <label className="col-sm-4 col-12 p-medium mt-2">
                                  First Name <span className="alert-star">*</span>
                                </label>
                                <div className="col-sm-8 col-12 d-flex flex-column">
                                  <Controller
                                    shouldUnregister
                                    name="contact_first_name"
                                    control={control}
                                    rules={{
                                      required: 'Required',
                                      pattern: {
                                        value: /^[a-zA-Z\s'-]+$/,
                                        message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                                      },
                                    }}
                                    render={({ field }) => (
                                      <TextSnippetWrapper
                                        {...field}
                                        type="text"
                                        value={field.value}
                                        placeholder="First Name"
                                        onChange={(e) => {
                                          const trimmedValue = e.target.value.trimStart();
                                          field.onChange(trimmedValue);
                                          trigger('contact_first_name');
                                        }}
                                        className="input-shadow w-100"
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_first_name'} />
                                </div>
                              </div>

                              <div className="d-flex py-2 flex-wrap align-items-start">
                                <label className="col-sm-4 col-12 p-medium mt-2">
                                  Last Name <span className="alert-star">*</span>
                                </label>
                                <div className="col-sm-8 col-12 d-flex flex-column">
                                  <Controller
                                    shouldUnregister
                                    name="contact_last_name"
                                    control={control}
                                    rules={{
                                      required: 'Required',
                                      pattern: {
                                        value: /^[a-zA-Z\s'-]+$/,
                                        message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                                      },
                                    }}
                                    render={({ field }) => (
                                      <TextSnippetWrapper
                                        type="text"
                                        value={field.value}
                                        placeholder="Last Name"
                                        onChange={(e) => {
                                          const trimmedValue = e.target.value.trimStart();
                                          field.onChange(trimmedValue);
                                          trigger('contact_last_name');
                                        }}
                                        className="input-shadow w-100"
                                        inputRef={field.ref}
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_last_name'} />
                                </div>
                              </div>

                              <div className="d-flex align-items-center py-2 flex-wrap">
                                <div className="col-sm-4 col-12 p-medium">
                                  Phone Number<span className="alert-star"> *</span>
                                </div>
                                <div className="col-sm-8 col-12">
                                  <Controller
                                    shouldUnregister
                                    name="contact_phone"
                                    control={control}
                                    rules={{
                                      required: 'Phone number is required',
                                      minLength: { value: 10, message: 'Min length should be 10' },
                                      validate: {
                                        asyncValidation: async (value) => {
                                          const isValid = await validatePhoneNumber(value, selectedContact?.contact_id);
                                          return isValid || 'Invalid phone number';
                                        },
                                      },
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                      <NumberFormat
                                        className="input-shadow w-100 p-2 number-format"
                                        placeholder="Enter Phone Number"
                                        format="(###) ###-####"
                                        mask="_"
                                        name="contact_phone"
                                        onValueChange={(e) => {
                                          handleContactPhoneValueChange(onChange, e.value);
                                        }}
                                        value={value}
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_phone'} />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div key="add-contact-business">
                              <div className="d-flex py-2 flex-wrap align-items-start">
                                <label className="col-sm-4 col-12 p-medium mt-2">
                                  Business Name <span className="alert-star">*</span>
                                </label>
                                <div className="col-sm-8 col-12 d-flex flex-column">
                                  <Controller
                                    shouldUnregister
                                    name="title"
                                    control={control}
                                    rules={{ required: 'Required' }}
                                    render={({ field }) => (
                                      <TextSnippetWrapper
                                        {...field}
                                        type="text"
                                        value={field.value}
                                        placeholder="Enter Business Name"
                                        onChange={(e) => {
                                          const trimmedValue = e.target.value.trimStart();
                                          field.onChange(trimmedValue);
                                          trigger('title');
                                        }}
                                        className="input-shadow w-100"
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'title'} />
                                </div>
                              </div>

                              <div className="d-flex align-items-center py-2 flex-wrap">
                                <div className="col-sm-4 col-12 p-medium">
                                  Phone Number<span className="alert-star"> *</span>
                                </div>
                                <div className="col-sm-8 col-12">
                                  <Controller
                                    shouldUnregister
                                    name="contact_phone"
                                    control={control}
                                    rules={{
                                      required: 'Phone number is required',
                                      minLength: { value: 10, message: 'Min length should be 10' },
                                      validate: {
                                        asyncValidation: async (value) => {
                                          const isValid = await validatePhoneNumber(value, selectedContact?.contact_id);
                                          return isValid || 'Invalid phone number';
                                        },
                                      },
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                      <NumberFormat
                                        className="input-shadow w-100 p-2 number-format"
                                        placeholder="Enter Phone Number"
                                        format="(###) ###-####"
                                        mask="_"
                                        name="contact_phone"
                                        onValueChange={(e) => {
                                          handleContactPhoneValueChange(onChange, e.value);
                                        }}
                                        value={value}
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_phone'} />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="d-flex align-items-center justify-content-end gap-2">
                        <Button
                          icon="pi pi-check"
                          className="p-button-secondary"
                          type="button"
                          onClick={() => {
                            handleCreateContact();
                          }}
                        />
                        <Button
                          icon="pi pi-times"
                          className="p-button-secondary outline"
                          type="button"
                          onClick={() => {
                            cancelAddContactFields();
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-sm-4 col-12  p-medium">
                      Call For <span className="alert-star"> *</span>
                    </div>
                    <div className="col-sm-8 col-12">
                      <Controller
                        name="call_for"
                        control={control}
                        rules={{ required: 'Call For is Required' }}
                        render={({ field: { value, onChange } }) => (
                          <MultiSelect
                            options={formatUserList() ?? []}
                            className="w-100 input-shadow"
                            filter
                            placeholder=""
                            filterPlaceholder="Search"
                            display="chip"
                            optionLabel="label"
                            optionValue={(v) => v}
                            value={value}
                            emptyFilterMessage="No records found"
                            onChange={(e) => onChange(e.value)}
                            resetFilterOnHide
                            showSelectAll={false}
                            itemTemplate={assignToCallForItemTemplate}
                          />
                        )}
                      />
                      <RequiredValidation field={'call_for'} />
                    </div>
                  </div>
                </>
              )}
              {watch('call_type') === 'outgoing' && (
                <>
                  {!showCreateContactFields ? (
                    <div>
                      <div className="d-flex align-items-center py-2 flex-wrap">
                        <div className="col-sm-4 col-12  p-medium">
                          Called To<span className="alert-star"> * </span>
                        </div>
                        <div className="col-sm-8 col-12">
                          <div className="d-flex align-items-center">
                            <div style={{ flexGrow: 1 }}>
                              <Controller
                                name="person_called"
                                control={control}
                                rules={{ required: 'Called By is Required', validate: autocompleteValidation }}
                                render={({ field: { onChange, value } }) => (
                                  <div className="position-relative">
                                    <AutoComplete
                                      placeholder="Search by Phone Number or Caller Name"
                                      style={{ width: '100%' }}
                                      inputStyle={{ width: '100%' }}
                                      suggestions={filteredClients}
                                      itemTemplate={itemTemplate}
                                      selectedItemTemplate={selectedItemTemplate}
                                      completeMethod={searchAsync}
                                      field="first_name"
                                      className="w-100 input-shadow"
                                      onChange={(e) => {
                                        onChange(e.value);
                                        setValue('phone', e?.value?.phone ? e?.value?.phone : '');
                                        setSelectedContact(e?.value);
                                      }}
                                      value={value}
                                      onBlur={() => typeof value !== 'object' && onChange('')}
                                    />
                                    {watch('person_called') !== '' &&
                                      selectedContact &&
                                      typeof selectedContact === 'object' &&
                                      selectedContact?.contact_type === 'business' && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 6,
                                            pointerEvents: 'none',
                                            background: 'transparent',
                                            zIndex: 2,
                                          }}
                                        >
                                          <Tag
                                            className="y-tag"
                                            value={
                                              selectedContact.contact_type.charAt(0).toUpperCase() + selectedContact.contact_type.slice(1)
                                            }
                                            severity="normal"
                                          ></Tag>
                                        </div>
                                      )}
                                  </div>
                                )}
                              />
                              <RequiredValidation field={'person_called'} />
                            </div>
                            <i
                              className="fas fa-plus-circle add-contact pointer ms-2"
                              onClick={() => {
                                setsShowCreateContactFields(true);
                              }}
                            ></i>
                            <Tooltip content="Add Contact" position="top" target=".add-contact" showDelay={500} />
                          </div>
                        </div>
                      </div>
                      {watch('person_called') !== '' && selectedContact && typeof selectedContact === 'object' && (
                        <div>
                          {selectedContact?.contact_type === 'business' && (
                            <div className="d-flex py-2 flex-wrap align-items-start">
                              <label className="col-sm-4 col-12 p-medium mt-2">
                                Caller Name <span className="alert-star">*</span>
                              </label>
                              <div className="col-sm-8 col-12 d-flex flex-column">
                                <Controller
                                  name="caller_name"
                                  control={control}
                                  rules={{ required: 'Required' }}
                                  render={({ field }) => (
                                    <TextSnippetWrapper
                                      {...field}
                                      type="text"
                                      value={field.value}
                                      placeholder="Enter Caller Name"
                                      onChange={(e) => {
                                        const trimmedValue = e.target.value.trimStart();
                                        field.onChange(trimmedValue);
                                        trigger('caller_name');
                                      }}
                                      className="input-shadow w-100"
                                    />
                                  )}
                                />
                                <RequiredValidation field={'caller_name'} />
                              </div>
                            </div>
                          )}

                          <div className="d-flex align-items-center py-2 flex-wrap">
                            <div className="col-sm-4 col-12 p-medium">
                              Phone Number<span className="alert-star"> *</span>
                            </div>
                            <div className="col-sm-8 col-12">
                              <Controller
                                name="phone"
                                control={control}
                                rules={{
                                  required: 'Phone number is required',
                                  minLength: { value: 10, message: 'Min length should be 10' },
                                  validate: {
                                    asyncValidation: async (value) => {
                                      const isValid = await validatePhoneNumber(value, selectedContact?.contact_id);
                                      return isValid || 'Invalid phone number';
                                    },
                                  },
                                }}
                                render={({ field: { onChange, value } }) => (
                                  <NumberFormat
                                    className="input-shadow w-100 p-2 number-format"
                                    placeholder="Phone"
                                    format="(###) ###-####"
                                    mask="_"
                                    name="phone"
                                    onValueChange={(e) => {
                                      handleExistingContactPhoneValueChange(onChange, e.value);
                                    }}
                                    value={value}
                                  />
                                )}
                              />
                              <RequiredValidation field={'phone'} />
                            </div>
                          </div>
                          {selectedContact?.contact_type === 'business' && (
                            <div className="d-flex py-2 flex-wrap align-items-start">
                              <label className="col-sm-4 col-12 p-medium mt-2">
                                Extension <span className="alert-star">*</span>
                              </label>
                              <div className="col-sm-8 col-12 d-flex flex-column">
                                <Controller
                                  name="contact_extension"
                                  control={control}
                                  rules={{ required: 'Required' }}
                                  render={({ field }) => (
                                    <TextSnippetWrapper
                                      {...field}
                                      type="text"
                                      value={field.value}
                                      placeholder="Enter Extension"
                                      onChange={(e) => {
                                        const trimmedValue = e.target.value.trimStart();
                                        field.onChange(trimmedValue);
                                        trigger('contact_extension');
                                      }}
                                      className="input-shadow w-100"
                                    />
                                  )}
                                />
                                <RequiredValidation field={'contact_extension'} />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="inner-card p-3 my-2">
                      <h5 className="sub-title mb-2">{'Add New Contact'}</h5>
                      <div>
                        <ContactDropdownField
                          control={control}
                          disabled={false}
                          getFormErrorMessage={getFormErrorMessage}
                          name={'contact_type'}
                          onChange={handleContactTypeChange}
                          options={types.filter((type) => type.value !== 'location')}
                          placeholder={'Type'}
                          rules={{ required: 'Required' }}
                        />
                      </div>
                      {contactType !== '' && (
                        <div>
                          {contactType === 'person' ? (
                            <div key="add-contact-person">
                              <div className="d-flex py-2 flex-wrap align-items-start">
                                <label className="col-sm-4 col-12 p-medium mt-2">
                                  Title <span className="alert-star">*</span>
                                </label>
                                <div className="col-sm-8 col-12">
                                  <Controller
                                    shouldUnregister
                                    name="contact_title"
                                    control={control}
                                    rules={{ required: 'Required' }}
                                    render={({ field: { name, value, onChange } }) => (
                                      <Dropdown
                                        id={name}
                                        options={titles}
                                        onChange={(e) => {
                                          onChange(e.target.value);
                                          trigger('contact_title');
                                        }}
                                        value={value}
                                        placeholder="Title"
                                        className="w-100"
                                        filter
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_title'} />
                                </div>
                              </div>

                              <div className="d-flex py-2 flex-wrap align-items-start">
                                <label className="col-sm-4 col-12 p-medium mt-2">
                                  First Name <span className="alert-star">*</span>
                                </label>
                                <div className="col-sm-8 col-12">
                                  <Controller
                                    shouldUnregister
                                    name="contact_first_name"
                                    control={control}
                                    rules={{
                                      required: 'Required',
                                      pattern: {
                                        value: /^[a-zA-Z\s'-]+$/,
                                        message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                                      },
                                    }}
                                    render={({ field }) => (
                                      <TextSnippetWrapper
                                        {...field}
                                        type="text"
                                        value={field.value}
                                        placeholder="First Name"
                                        onChange={(e) => {
                                          const trimmedValue = e.target.value.trimStart();
                                          field.onChange(trimmedValue);
                                          trigger('contact_first_name');
                                        }}
                                        className="input-shadow w-100"
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_first_name'} />
                                </div>
                              </div>

                              <div className="d-flex py-2 flex-wrap align-items-start">
                                <label className="col-sm-4 col-12 p-medium mt-2">
                                  Last Name <span className="alert-star">*</span>
                                </label>
                                <div className="col-sm-8 col-12">
                                  <Controller
                                    shouldUnregister
                                    name="contact_last_name"
                                    control={control}
                                    rules={{
                                      required: 'Required',
                                      pattern: {
                                        value: /^[a-zA-Z\s'-]+$/,
                                        message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                                      },
                                    }}
                                    render={({ field }) => (
                                      <TextSnippetWrapper
                                        type="text"
                                        value={field.value}
                                        placeholder="Last Name"
                                        onChange={(e) => {
                                          const trimmedValue = e.target.value.trimStart();
                                          field.onChange(trimmedValue);
                                          trigger('contact_last_name');
                                        }}
                                        className="input-shadow w-100"
                                        inputRef={field.ref}
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_last_name'} />
                                </div>
                              </div>

                              <div className="d-flex align-items-center py-2 flex-wrap">
                                <div className="col-sm-4 col-12 p-medium">
                                  Phone Number<span className="alert-star"> *</span>
                                </div>
                                <div className="col-sm-8 col-12">
                                  <Controller
                                    shouldUnregister
                                    name="contact_phone"
                                    control={control}
                                    rules={{
                                      required: 'Phone number is required',
                                      minLength: { value: 10, message: 'Min length should be 10' },
                                      validate: {
                                        asyncValidation: async (value) => {
                                          const isValid = await validatePhoneNumber(value, selectedContact?.contact_id);
                                          return isValid || 'Invalid phone number';
                                        },
                                      },
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                      <NumberFormat
                                        className="input-shadow w-100 p-2 number-format"
                                        placeholder="Enter Phone Number"
                                        format="(###) ###-####"
                                        mask="_"
                                        name="contact_phone"
                                        onValueChange={(e) => {
                                          handleContactPhoneValueChange(onChange, e.value);
                                        }}
                                        value={value}
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_phone'} />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div key="add-contact-business">
                              <div className="d-flex py-2 flex-wrap align-items-start">
                                <label className="col-sm-4 col-12 p-medium mt-2">
                                  Business Name <span className="alert-star">*</span>
                                </label>
                                <div className="col-sm-8 col-12 d-flex flex-column">
                                  <Controller
                                    shouldUnregister
                                    name="title"
                                    control={control}
                                    rules={{ required: 'Required' }}
                                    render={({ field }) => (
                                      <TextSnippetWrapper
                                        {...field}
                                        type="text"
                                        value={field.value}
                                        placeholder="Enter Business Name"
                                        onChange={(e) => {
                                          const trimmedValue = e.target.value.trimStart();
                                          field.onChange(trimmedValue);
                                          trigger('title');
                                        }}
                                        className="input-shadow w-100"
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'title'} />
                                </div>
                              </div>

                              <div className="d-flex align-items-center py-2 flex-wrap">
                                <div className="col-sm-4 col-12 p-medium">
                                  Phone Number<span className="alert-star"> *</span>
                                </div>
                                <div className="col-sm-8 col-12">
                                  <Controller
                                    shouldUnregister
                                    name="contact_phone"
                                    control={control}
                                    rules={{
                                      required: 'Phone number is required',
                                      minLength: { value: 10, message: 'Min length should be 10' },
                                      validate: {
                                        asyncValidation: async (value) => {
                                          const isValid = await validatePhoneNumber(value, selectedContact?.contact_id);
                                          return isValid || 'Invalid phone number';
                                        },
                                      },
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                      <NumberFormat
                                        className="input-shadow w-100 p-2 number-format"
                                        placeholder="Enter Phone Number"
                                        format="(###) ###-####"
                                        mask="_"
                                        name="contact_phone"
                                        onValueChange={(e) => {
                                          handleContactPhoneValueChange(onChange, e.value);
                                        }}
                                        value={value}
                                      />
                                    )}
                                  />
                                  <RequiredValidation field={'contact_phone'} />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="d-flex align-items-center justify-content-end gap-2">
                        <Button
                          icon="pi pi-check"
                          className="p-button-secondary"
                          type="button"
                          onClick={() => {
                            handleCreateContact();
                          }}
                        />
                        <Button
                          icon="pi pi-times"
                          className="p-button-secondary outline"
                          type="button"
                          onClick={() => {
                            cancelAddContactFields();
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="d-flex align-items-center py-2 flex-wrap gap-2 gap-md-0">
                <div className="col-sm-4 p-medium">
                  <label htmlFor="cb1" className="p-checkbox-label">
                    Need Resolution
                  </label>
                </div>
                <div className="col-sm-8 d-flex align-items-center ml-sm-2">
                  <Controller
                    name="resolution_status"
                    control={control}
                    defaultValue={false}
                    render={({ field: { ref, name, onChange, value }, fieldState }) => (
                      <Checkbox
                        inputId={name}
                        ref={ref}
                        onChange={(e) => onChange(e.checked)}
                        checked={value}
                        className={{ 'p-invalid': fieldState.invalid }}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-sm-4 col-12 p-medium">
                  Assign to Firm User {watch('resolution_status') && <span className="alert-star"> *</span>}
                </div>
                <div className="col-sm-8 col-12">
                  <Controller
                    name="assign_to"
                    control={control}
                    rules={{ required: watch('resolution_status') ? 'Assignee is Required' : false }}
                    render={({ field: { value, onChange } }) => (
                      <MultiSelect
                        options={formatUserList() ?? []}
                        className="w-100 input-shadow"
                        filter
                        filterPlaceholder="Search"
                        placeholder="Select"
                        display="chip"
                        optionLabel="label"
                        optionValue={(v) => v}
                        value={value}
                        onChange={(e) => onChange(e.value)}
                        resetFilterOnHide
                        showSelectAll={false}
                        emptyFilterMessage="No records found"
                        itemTemplate={assignToCallLogItemTemplate}
                        disabled={!resolutionStatus}
                      />
                    )}
                  />
                  <RequiredValidation field={'assign_to'} />
                </div>
              </div>

              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-sm-4 col-12 p-medium">
                  Call Date<span className="alert-star"> *</span>
                </div>
                <div className="col-sm-8 col-12">
                  <Controller
                    name="call_date"
                    control={control}
                    rules={{ required: true }}
                    defaultValue={new Date()}
                    render={({ field: { name, value, onChange } }) => (
                      <Calendar
                        className="w-100 input-shadow"
                        monthNavigator
                        yearNavigator
                        yearRange="1900:3000"
                        id={name}
                        value={value}
                        onChange={(e) => onChange(e.value)}
                        showButtonBar
                        showTime
                      />
                    )}
                  />
                  <RequiredValidation field={'call_date'} />
                </div>
              </div>
            </>
          )}

          <div className="footer-btn">
            <Button label="Cancel" className="p-button-secondary outline" onClick={handleClose} />
            <Button
              type="button"
              label="Save"
              className="p-button p-component-button p-button-secondary ms-2"
              onClick={handleSubmit(handleSaveNote)}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default NotesCreate;
